import React, { ReactElement } from 'react';
import { useIntl, FormattedDate } from 'react-intl';
import Table, {
  TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@fuww/library/src/Table';
import IntelligencePageLayout from '@fuww/library/src/Layouts/IntelligencePage';
import SpacingContainer from '@fuww/library/src/SpacingContainer';
import Layout from '../components/Layout';
import messages from '../lib/messages.json';
import Head from '../components/Head';
import Hero from '../components/Hero';
import top100Companies from '../data/staticPages/i/top100.json';
import formatBigMoney from '../lib/formatBigMoney';
import generateIntelligencePageBreadcrumbs
  from '../lib/generateIntelligencePageBreadcrumbs';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';
import LinkAndAnchor from '../components/LinkAndAnchor';

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/UUkjKNo6SrE2G-fXqA0o5IXnLWFcCbRF7hwa9oK-a68/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/xs96OPdhXg7C_MHWys2YF_l2vvsG7bIkSfJAVKKk_VI/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/cI8Kvl8dYP2edIEWZqKe6YINxatZfewpvgzpWd2yZFo/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfbW9iaWxlLmpwZw',
  ],
  desktop: [
    'https://r.fashionunited.com/AreUw-4DZXb_jLrOFDuSEBYsxAsPtXy2ybFg4KtFwkU/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/8mGlxYLhFghIE2wXD_521ESS-kSAAu1Gux7_W5vXW7Q/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/4fdeaBNtYMx4dN2iMWaZhGatSYtRWQI0WpZ3ESJzCyE/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/fO9xFGDw2PUvq9F8466_SdZwkmECzWuN_y1dM6WIhuo/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvdG9wXzEwMF9iYW5uZXJfZGVza3RvcC5qcGc',
  ],
};

const Top100Page = () => {
  const intl = useIntl();

  const breadcrumbs = generateIntelligencePageBreadcrumbs(
    intl,
    'top100',
    'top100',
  );

  return (
    <>
      <Head
        title={intl.formatMessage(messages['top100.title'])}
        description={intl.formatMessage(messages['top100.description'])}
      />
      <IntelligencePageLayout
        description={
          intl.formatMessage(messages['top100.description'])
        }
        content={(
          <>
            <TableContainer>
              <Table aria-label={intl.formatMessage(
                messages['top100.table.ariaLabel'],
              )}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>
                      {intl.formatMessage(messages.country)}
                    </TableCell>
                    <TableCell>{intl.formatMessage(messages.name)}</TableCell>
                    <TableCell align="right">
                      {intl.formatMessage(messages.marketCap)}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage(messages['top100.ceo'])}
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage(messages['top100.ceoStartDate'])}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {top100Companies.map(({
                    country,
                    name,
                    marketCap,
                    slug,
                    ceoName,
                    ceoStartDate,
                  }, index) => (
                    <TableRow key={name}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{country}</TableCell>
                      <TableCell>
                        <LinkAndAnchor
                          route="tag"
                          params={{ slug }}
                          styled={false}
                        >
                          {name}
                        </LinkAndAnchor>
                      </TableCell>
                      <TableCell align="right">
                        {formatBigMoney(marketCap)}
                      </TableCell>
                      <TableCell>{ceoName}</TableCell>
                      <TableCell>
                        <FormattedDate value={ceoStartDate} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <SpacingContainer />
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{
              __html: intl.formatMessage(messages[
                'top100.source'
              ]),
            }}
            />
            <SpacingContainer />
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{
              __html: intl.formatMessage(messages[
                'top100.disclaimer'
              ]),
            }}
            />
          </>
        )}
        breadcrumbs={breadcrumbs}
        subTitle={intl.formatMessage(messages['top100.subTitle'])}
        title={intl.formatMessage(messages['top100.title'])}
      />
    </>
  );
};

Top100Page.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        imageUrls={headerImageUrls}
        alt="Top 100 companies banner image"
      />
    )}
  >
    {page}
  </Layout>
);

Top100Page.getInitialProps = redirectRouteToDomain('top100');

export default Top100Page;
